import React, { useRef, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Button } from "antd";
import { Eraser } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";

/**
 * Reusable signature capture component.
 * Used in onboarding, rental agreement settings, and insurance connection flows.
 */
const SignaturePad = ({ onSave, onClear, width = 600, t }) => {
    const signatureRef = useRef(null);
    const containerRef = useRef(null);
    const signatureDataRef = useRef(null);
    const [isEmpty, setIsEmpty] = useState(true);
    const [canvasWidth, setCanvasWidth] = useState(width);

    const persistSignatureSnapshot = useCallback(() => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            signatureDataRef.current = signatureRef.current.toDataURL("image/png");
        }
    }, []);

    const restoreSignatureSnapshot = useCallback(() => {
        const pad = signatureRef.current;
        const snapshot = signatureDataRef.current;
        if (!pad || !snapshot) {
            return;
        }

        pad.fromDataURL(snapshot, () => {
            setIsEmpty(pad.isEmpty());
        });
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const updateCanvasWidth = () => {
            const nextWidth = Math.max(Math.floor(container.clientWidth), 240);
            setCanvasWidth((prev) => {
                if (prev === nextWidth) {
                    return prev;
                }
                persistSignatureSnapshot();
                return nextWidth;
            });
        };

        updateCanvasWidth();
        const observer = new ResizeObserver(updateCanvasWidth);
        observer.observe(container);
        return () => observer.disconnect();
    }, [persistSignatureSnapshot, width]);

    useEffect(() => {
        restoreSignatureSnapshot();
    }, [canvasWidth, restoreSignatureSnapshot]);

    // Helper function to convert signature to file
    const convertSignatureToFile = useCallback((dataUrl, callback) => {
        // Get canvas element and convert to blob/file
        const canvas = signatureRef.current?.getCanvas();
        if (canvas && canvas.toBlob) {
            canvas.toBlob(
                (blob) => {
                    const file = new File([blob], `signature-${Date.now()}.png`, { type: "image/png" });
                    callback(file, dataUrl);
                },
                "image/png",
                1.0,
            );
        } else {
            // Fallback: convert data URL to blob manually
            fetch(dataUrl)
                .then((res) => res.blob())
                .then((blob) => {
                    const file = new File([blob], `signature-${Date.now()}.png`, { type: "image/png" });
                    callback(file, dataUrl);
                })
                .catch(() => {
                    // Error converting signature to file
                });
        }
    }, []);

    const handleSignatureEnd = useCallback(() => {
        if (signatureRef.current) {
            const isEmptyNow = signatureRef.current.isEmpty();
            setIsEmpty(isEmptyNow);
            if (!isEmptyNow && onSave) {
                const dataUrl = signatureRef.current.toDataURL("image/png");
                signatureDataRef.current = dataUrl;
                convertSignatureToFile(dataUrl, onSave);
            } else if (isEmptyNow) {
                signatureDataRef.current = null;
            }
        }
    }, [onSave, convertSignatureToFile]);

    const clearCanvas = useCallback(() => {
        if (signatureRef.current) {
            signatureRef.current.clear();
            signatureDataRef.current = null;
            setIsEmpty(true);
            onClear?.();
        }
    }, [onClear]);

    return (
        <div className="flex min-w-0 flex-col gap-3">
            <div
                ref={containerRef}
                className="relative w-full max-w-full border border-[#B6B3C8] rounded-lg overflow-hidden bg-white">
                <SignatureCanvas
                    ref={signatureRef}
                    penColor="blue"
                    backgroundColor="white"
                    clearOnResize={false}
                    canvasProps={{
                        width: canvasWidth,
                        height: 150,
                        className: "sigCanvas",
                    }}
                    onEnd={handleSignatureEnd}
                />
                <Button
                    onClick={clearCanvas}
                    icon={<Eraser className="text-white" size={16} />}
                    size="small"
                    type="primary"
                    className="absolute top-2 right-2 z-20 rounded-full px-4 py-3"
                    style={{ backgroundColor: "#5D5DFF", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", color: "white" }}>
                    {t?.("clear") || "Clear"}
                </Button>
                {isEmpty && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                        <span className="text-gray-400 text-sm">{t?.("signHere") || "Sign here"}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

SignaturePad.propTypes = {
    onSave: PropTypes.func,
    onClear: PropTypes.func,
    width: PropTypes.number,
    t: PropTypes.func,
};

export default SignaturePad;
